import React, { useMemo, useState } from "react";
import {
    Plus,
    Search,
    Upload,
    MessageSquare,
    ShoppingBag,
    ShoppingCart,
    BarChart3,
    CalendarDays,
    Building2,
    Mail,
    CloudSun,
    Loader2,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";

// Visual preview templates — each card renders a tiny mockup of the actual widget
const TEMPLATES = [
    {
        key: "book_a_demo",
        title: "Book a demo",
        description: "Capture sales leads with a contact form in chat.",
        type: "Form",
        Preview: BookDemoPreview,
    },
    {
        key: "buy_products",
        title: "Buy products",
        description: "Show a shoppable product list with images, prices and add-to-cart.",
        type: "Catalog",
        Preview: BuyProductsPreview,
    },
    {
        key: "get_cart",
        title: "Get cart",
        description: "Display cart items and totals. Checkout without leaving the conversation.",
        type: "Commerce",
        Preview: GetCartPreview,
    },
    {
        key: "usage_chart",
        title: "Usage chart",
        description: "Plot a metric across days as a compact bar chart inside chat.",
        type: "Analytics",
        Preview: UsageChartPreview,
    },
    {
        key: "upcoming_classes",
        title: "Upcoming events",
        description: "RSVP-style cards for yoga, dining and other on-site events.",
        type: "Schedule",
        Preview: UpcomingClassesPreview,
    },
    {
        key: "book_stay",
        title: "Book a stay",
        description: "Inline booking card with room imagery for an Orchid property.",
        type: "Booking",
        Preview: BookStayPreview,
    },
    {
        key: "get_in_touch",
        title: "Get in touch",
        description: "Contact form rendered as a card in the chat thread.",
        type: "Form",
        Preview: GetInTouchPreview,
    },
    {
        key: "weather",
        title: "Weather",
        description: "Show the destination weather forecast next to recommendations.",
        type: "Info",
        Preview: WeatherPreview,
    },
];

const TYPE_FILTERS = ["All types", "Form", "Catalog", "Commerce", "Analytics", "Schedule", "Booking", "Info"];

export default function Widgets() {
    const [tab, setTab] = useState("create");
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("All types");
    const [customOpen, setCustomOpen] = useState(false);
    const [customDraft, setCustomDraft] = useState({ name: "", description: "", html: "" });
    const [savingCustom, setSavingCustom] = useState(false);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return TEMPLATES.filter((t) => {
            if (typeFilter !== "All types" && t.type !== typeFilter) return false;
            if (!q) return true;
            return (
                t.title.toLowerCase().includes(q) ||
                t.description.toLowerCase().includes(q)
            );
        });
    }, [search, typeFilter]);

    const installTemplate = async (t) => {
        try {
            await adminApi.post("/api/admin/beta/request", {
                feature: `widget_${t.key}`,
                notes: `Wants to install widget: ${t.title}`,
            });
            toast.success(`Added "${t.title}" — Priya will start showing it shortly.`);
        } catch (err) {
            toast.error("Failed to install widget");
        }
    };

    const saveCustom = async () => {
        const name = customDraft.name.trim();
        if (!name) {
            toast.error("Widget name is required");
            return;
        }
        setSavingCustom(true);
        try {
            await adminApi.post("/api/admin/beta/request", {
                feature: "widget_custom",
                notes: `Custom widget request: name=${name}; desc=${customDraft.description}; html_len=${customDraft.html.length}`,
            });
            toast.success("Custom widget submitted — our team will reach out.");
            setCustomOpen(false);
            setCustomDraft({ name: "", description: "", html: "" });
        } catch (err) {
            toast.error("Submit failed");
        } finally {
            setSavingCustom(false);
        }
    };

    return (
        <div className="p-10 max-w-7xl" data-testid="page-widgets">
            <div className="mb-6">
                <h1 className="font-heading text-4xl text-stone-900">Widgets</h1>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 border-b border-stone-200 mb-8">
                {[
                    { k: "mine", label: "My widgets" },
                    { k: "create", label: "Create widgets" },
                ].map((t) => (
                    <button
                        key={t.k}
                        onClick={() => setTab(t.k)}
                        data-testid={`widgets-tab-${t.k}`}
                        className={`px-4 py-2.5 -mb-px text-sm font-medium border-b-2 transition-colors ${
                            tab === t.k
                                ? "border-stone-900 text-stone-900"
                                : "border-transparent text-stone-500 hover:text-stone-700"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {/* Toolbar */}
            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-2.5 text-stone-400" size={14} />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search templates…"
                        data-testid="widgets-search"
                        className="w-full pl-9 pr-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                    />
                </div>
                <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    data-testid="widgets-type-filter"
                    className="px-3 py-2 rounded-md border border-stone-200 text-sm bg-white"
                >
                    {TYPE_FILTERS.map((t) => (
                        <option key={t}>{t}</option>
                    ))}
                </select>
                <div className="ml-auto flex gap-2">
                    <button
                        title="Import widget JSON"
                        data-testid="widgets-import"
                        onClick={() => toast.info("Import is part of the Pro plan.")}
                        className="p-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                    >
                        <Upload size={14} />
                    </button>
                    <button
                        onClick={() => setCustomOpen(true)}
                        data-testid="widgets-custom"
                        className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-800 text-white text-sm font-medium px-4 py-2 rounded-md"
                    >
                        <Plus size={14} /> Custom widget
                    </button>
                </div>
            </div>

            {tab === "create" ? (
                <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5"
                    data-testid="widgets-templates"
                >
                    {filtered.map((t) => (
                        <div
                            key={t.key}
                            data-testid={`widget-template-${t.key}`}
                            className="bg-white border border-stone-200 rounded-xl overflow-hidden hover:border-stone-900 transition-colors"
                        >
                            <div className="bg-stone-50 h-56 p-3 flex items-center justify-center">
                                <t.Preview />
                            </div>
                            <div className="p-4">
                                <div className="font-medium text-stone-900 text-sm mb-1">{t.title}</div>
                                <p className="text-xs text-stone-600 leading-relaxed line-clamp-2 mb-3">
                                    {t.description}
                                </p>
                                <button
                                    onClick={() => installTemplate(t)}
                                    data-testid={`widget-install-${t.key}`}
                                    className="text-xs font-medium text-orchid-700 hover:text-orchid-600 inline-flex items-center gap-1.5"
                                >
                                    <Plus size={12} /> Add to bot
                                </button>
                            </div>
                        </div>
                    ))}
                    {filtered.length === 0 && (
                        <div className="col-span-full text-center text-stone-500 py-12 text-sm">
                            No templates match your filters.
                        </div>
                    )}
                </div>
            ) : (
                <div
                    className="bg-white border border-stone-200 rounded-xl p-16 text-center"
                    data-testid="widgets-empty"
                >
                    <div className="w-14 h-14 rounded-xl bg-orchid-700/10 text-orchid-700 mx-auto flex items-center justify-center mb-4">
                        <MessageSquare size={22} />
                    </div>
                    <h3 className="font-heading text-2xl text-orchid-700">
                        No widgets installed yet
                    </h3>
                    <p className="text-sm text-stone-500 mt-2 mb-6 max-w-md mx-auto">
                        Pick a template on the right to add a rich UI surface to Priya — forms, product cards, booking flows or analytics charts.
                    </p>
                    <button
                        onClick={() => setTab("create")}
                        data-testid="widgets-create-cta"
                        className="inline-flex items-center gap-2 bg-orchid-700 hover:bg-orchid-600 text-white text-sm font-medium px-5 py-2.5 rounded-md"
                    >
                        <Plus size={15} /> Browse templates
                    </button>
                </div>
            )}

            {customOpen && (
                <div
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
                    onClick={() => setCustomOpen(false)}
                    data-testid="custom-widget-modal"
                >
                    <div
                        className="bg-white rounded-xl w-full max-w-lg p-6 shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <h3 className="font-heading text-2xl text-orchid-700 mb-1">Custom widget</h3>
                        <p className="text-xs text-stone-500 mb-5">
                            Describe what you need — our team will build, test and roll it out for Priya within 48 hours.
                        </p>
                        <div className="space-y-3">
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 block">
                                    Widget name
                                </label>
                                <input
                                    value={customDraft.name}
                                    onChange={(e) =>
                                        setCustomDraft({ ...customDraft, name: e.target.value })
                                    }
                                    data-testid="custom-name"
                                    placeholder="E.g. Pet-friendly properties picker"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 block">
                                    What should it do?
                                </label>
                                <textarea
                                    rows={3}
                                    value={customDraft.description}
                                    onChange={(e) =>
                                        setCustomDraft({
                                            ...customDraft,
                                            description: e.target.value,
                                        })
                                    }
                                    data-testid="custom-desc"
                                    placeholder="A card that lets guests filter Orchid properties by pet-friendly, wedding-ready, near airport, etc."
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm focus:outline-none focus:border-orchid-700"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-wider text-stone-500 mb-1 block">
                                    Mockup or existing HTML (optional)
                                </label>
                                <textarea
                                    rows={4}
                                    value={customDraft.html}
                                    onChange={(e) =>
                                        setCustomDraft({ ...customDraft, html: e.target.value })
                                    }
                                    placeholder="<div>…</div>"
                                    className="w-full px-3 py-2 rounded-md border border-stone-200 text-sm font-mono focus:outline-none focus:border-orchid-700"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end gap-2 mt-5">
                            <button
                                onClick={() => setCustomOpen(false)}
                                className="text-sm px-4 py-2 rounded-md border border-stone-200 text-stone-700 hover:bg-stone-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={saveCustom}
                                disabled={savingCustom || !customDraft.name.trim()}
                                data-testid="custom-submit"
                                className="text-sm font-medium px-4 py-2 rounded-md bg-orchid-700 hover:bg-orchid-600 text-white disabled:opacity-50 inline-flex items-center gap-2"
                            >
                                {savingCustom ? (
                                    <Loader2 size={13} className="animate-spin" />
                                ) : null}
                                Submit request
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ===== Tiny visual previews =====

function BookDemoPreview() {
    return (
        <div className="w-full bg-white rounded-md border border-stone-200 p-3 text-left">
            <div className="font-medium text-xs text-stone-900 mb-2">Talk to sales</div>
            <div className="text-[9px] text-stone-500 mb-2">
                Tell us about your company and we&apos;ll get in touch.
            </div>
            <div className="text-[8px] text-stone-500 mb-0.5">Name</div>
            <div className="h-6 bg-stone-100 rounded mb-2 flex items-center px-1.5 text-[8px] text-stone-500">
                John Doe
            </div>
            <div className="text-[8px] text-stone-500 mb-0.5">Work email</div>
            <div className="h-6 bg-stone-100 rounded mb-2 flex items-center px-1.5 text-[8px] text-stone-500">
                you@example.com
            </div>
            <div className="text-[8px] text-stone-500 mb-0.5">Company size</div>
            <div className="h-6 bg-stone-100 rounded" />
        </div>
    );
}

function BuyProductsPreview() {
    return (
        <div className="w-full bg-white rounded-md border border-stone-200 p-2">
            <div className="text-[9px] text-stone-500 mb-1.5">
                <ShoppingBag size={9} className="inline mr-1" />
                Select products
            </div>
            <div className="bg-stone-100 rounded h-20 mb-2 flex items-center justify-center text-[8px] text-stone-400">
                Product image
            </div>
            <div className="font-medium text-[10px] text-stone-900">XY Metcon 64</div>
            <div className="text-[8px] text-stone-500">Men&apos;s Workout Shoes</div>
            <div className="flex items-center justify-between mt-1.5">
                <div className="text-[10px] font-medium text-stone-900">$16.00</div>
                <div className="text-[8px] bg-stone-900 text-white rounded px-1.5 py-0.5">Add</div>
            </div>
        </div>
    );
}

function GetCartPreview() {
    return (
        <div className="w-full bg-white rounded-md border border-stone-200 p-2.5">
            <div className="text-[9px] text-stone-700 mb-2">
                <ShoppingCart size={9} className="inline mr-1" /> 2 items in cart
            </div>
            <div className="space-y-1.5">
                {[
                    { name: "XY Metcon 64", price: "$16.00" },
                    { name: "XY V2 Run", price: "$24.90" },
                ].map((p) => (
                    <div key={p.name} className="flex items-center gap-2">
                        <div className="w-7 h-7 bg-stone-100 rounded" />
                        <div className="flex-1 text-[8px] text-stone-700">{p.name}</div>
                        <div className="text-[9px] font-medium">{p.price}</div>
                    </div>
                ))}
            </div>
            <div className="border-t border-stone-100 mt-2 pt-1.5 flex justify-between text-[9px]">
                <span className="text-stone-500">Total</span>
                <span className="font-medium">$40.90</span>
            </div>
            <div className="mt-2 bg-stone-900 text-white rounded text-center text-[9px] py-1.5">
                Checkout
            </div>
        </div>
    );
}

function UsageChartPreview() {
    const heights = [38, 22, 30, 36, 12, 28, 40];
    return (
        <div className="w-full bg-white rounded-md border border-stone-200 p-3">
            <div className="text-[9px] text-stone-500 mb-1 flex items-center gap-1">
                <BarChart3 size={9} /> Usage
            </div>
            <div className="text-[8px] text-stone-500">Total credits used</div>
            <div className="text-xl font-bold text-stone-900 mb-2">2214</div>
            <div className="flex items-end justify-between h-14 gap-1.5">
                {heights.map((h, i) => (
                    <div
                        key={i}
                        className="bg-indigo-400 rounded-sm flex-1"
                        style={{ height: `${h * 1.5}%` }}
                    />
                ))}
            </div>
            <div className="flex justify-between text-[7px] text-stone-400 mt-1">
                <span>S</span>
                <span>M</span>
                <span>T</span>
                <span>W</span>
                <span>T</span>
                <span>F</span>
                <span>S</span>
            </div>
        </div>
    );
}

function UpcomingClassesPreview() {
    return (
        <div className="w-full bg-white rounded-md border border-stone-200 p-2.5">
            <div className="text-[9px] text-stone-500 mb-2 flex items-center gap-1">
                <CalendarDays size={9} /> Upcoming classes
            </div>
            <div className="border border-stone-100 rounded p-1.5 mb-1.5 flex gap-2">
                <div className="w-10 h-10 bg-emerald-200 rounded shrink-0" />
                <div className="flex-1">
                    <div className="text-[10px] font-medium">Zumba Burn</div>
                    <div className="text-[8px] text-stone-500">Beginner · Mon 6 PM</div>
                </div>
            </div>
            <div className="border border-stone-100 rounded p-1.5 flex gap-2">
                <div className="w-10 h-10 bg-amber-200 rounded shrink-0" />
                <div className="flex-1">
                    <div className="text-[10px] font-medium">Sunrise Yoga</div>
                    <div className="text-[8px] text-stone-500">All levels · Tue 6 AM</div>
                </div>
            </div>
        </div>
    );
}

function BookStayPreview() {
    return (
        <div className="w-full bg-white rounded-md border border-stone-200 p-2">
            <div className="bg-stone-200 rounded h-20 mb-2 flex items-center justify-center text-[8px] text-stone-500">
                <Building2 size={14} />
            </div>
            <div className="text-[10px] font-medium text-stone-900">The Orchid Mumbai</div>
            <div className="text-[8px] text-stone-500">Eco-luxe stay · Vile Parle</div>
            <div className="mt-1.5 bg-orchid-700 text-white rounded text-center text-[9px] py-1.5">
                Book a stay
            </div>
        </div>
    );
}

function GetInTouchPreview() {
    return (
        <div className="w-full bg-white rounded-md border border-stone-200 p-3">
            <div className="text-[10px] font-medium text-stone-900 mb-0.5">Get in Touch</div>
            <div className="text-[8px] text-stone-500 mb-2">We&apos;d love to hear from you</div>
            <Mail size={12} className="text-stone-400 mb-1" />
            <div className="text-[8px] text-stone-500 mb-0.5">Name</div>
            <div className="h-5 bg-stone-100 rounded mb-1.5" />
            <div className="text-[8px] text-stone-500 mb-0.5">Email</div>
            <div className="h-5 bg-stone-100 rounded" />
        </div>
    );
}

function WeatherPreview() {
    return (
        <div className="w-full h-full rounded-md p-3 text-white flex flex-col justify-between bg-gradient-to-br from-sky-400 to-blue-500">
            <CloudSun size={20} />
            <div>
                <div className="text-3xl font-bold">72°</div>
                <div className="text-[10px] opacity-90">San Francisco</div>
            </div>
        </div>
    );
}
